import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Warehouse, ScanLine, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Giriş başarısız");
    else navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_520px]">
      {/* Left: warehouse image + overlay */}
      <div
        className="relative hidden lg:block grain"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(10,10,11,0.85), rgba(0,51,160,0.55)), url("https://images.pexels.com/photos/36217325/pexels-photo-36217325.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/brand/stargrup-logo.png"
              alt="StarGrup"
              className="h-12 w-auto bg-white/95 hard px-3 py-1"
            />
            <div className="ticker">CAM · STOK · KONTROL</div>
          </div>

          <div className="max-w-lg">
            <div className="ticker mb-4 text-[color:var(--accent)]">
              /// ADRESLEME DEFTERİ
            </div>
            <h1 className="text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
              Barkod okut.
              <br />
              Cam gir. Cam çıkar.
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Endüstriyel hız için tasarlanmış cam depo yönetim sistemi.
              USB barkod okuyucu veya telefon kameranız ile tek dokunuşta
              toplu giriş ve çıkış yapın.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/20 pt-6">
              <div>
                <div className="text-3xl font-black">2262+</div>
                <div className="ticker text-white/50 mt-1">Kayıt</div>
              </div>
              <div>
                <div className="text-3xl font-black">USB</div>
                <div className="ticker text-white/50 mt-1">+ Kamera</div>
              </div>
              <div>
                <div className="text-3xl font-black">JWT</div>
                <div className="ticker text-white/50 mt-1">Güvenli</div>
              </div>
            </div>
          </div>

          <div className="ticker text-white/40">
            v1.0 · Endüstriyel Sınıf Envanter Kontrolü
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[color:var(--surface)] border-l border-[color:var(--border)]">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img
              src="/brand/stargrup-logo.png"
              alt="StarGrup"
              className="h-10 w-auto"
            />
            <div className="ticker">CAM STOK KONTROL</div>
          </div>

          <div className="ticker text-[color:var(--text-3)] mb-2">
            /// OTURUM AÇ
          </div>
          <h2 className="text-3xl font-black tracking-tighter mb-1">
            Kontrol paneline erişin
          </h2>
          <p className="text-sm text-[color:var(--text-2)] mb-8">
            Yetkilendirilmiş operatör hesabınızla giriş yapın.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label
                className="ticker block mb-2 text-[color:var(--text-2)]"
                htmlFor="login-email-input"
              >
                E-Posta
              </label>
              <input
                id="login-email-input"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@cam.local"
                className="w-full h-12 hard border-2 border-[color:var(--border)] bg-white px-3 outline-none focus:border-[color:var(--primary)] transition-colors"
                autoComplete="username"
              />
            </div>

            <div>
              <label
                className="ticker block mb-2 text-[color:var(--text-2)]"
                htmlFor="login-password-input"
              >
                Şifre
              </label>
              <input
                id="login-password-input"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-12 hard border-2 border-[color:var(--border)] bg-white px-3 outline-none focus:border-[color:var(--primary)] transition-colors"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="hard border border-[color:var(--error)] bg-[color:var(--error-bg)] text-[color:var(--error)] px-3 py-2 text-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
              <ArrowRight size={18} strokeWidth={2.5} />
            </button>
          </form>

          <div className="mt-10 hard border border-[color:var(--border)] bg-[color:var(--bg)] p-4 flex items-start gap-3">
            <ScanLine size={20} className="text-[color:var(--accent)] mt-0.5" />
            <div className="text-xs text-[color:var(--text-2)] leading-relaxed">
              <div className="ticker text-[color:var(--text)] mb-1">
                Demo Erişimi
              </div>
              admin@cam.local · admin123
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
